import { useState, useRef, useEffect } from 'react';
import { format } from 'date-fns';
import {
  RefreshCw,
  Check,
  X,
  ChevronDown,
  ChevronUp,
  Calendar,
  Banknote,
  TrendingUp,
  Eye,
} from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { useLocale } from '../../../shared/hooks/useLocale';
import type { ProposedEntityGroup, ProposedPaymentDetail, RecurringType } from '../../recurring/api/useRecurring';

/**
 * Legacy flat item — kept for backward-compat imports but no longer
 * used by the grouped card.
 */
export interface ProposedPaymentItem {
  paymentId: string;
  recurringEntityId: string;
  entityName: string;
  entityType: string;
  transactionId: string;
  expectedAmount: number;
  actualAmount: number;
  period: string;
  paidAt: string;
  transactionPayee?: string;
  transactionDescription?: string;
}

interface RecurringGroupCardProps {
  group: ProposedEntityGroup;
  onConfirm: (entityId: string, paymentId: string, type?: RecurringType) => void;
  onReject: (entityId: string, paymentId: string) => void;
  onViewEntity: (entityId: string) => void;
  confirmingPaymentId: string | null;
  rejectingPaymentId: string | null;
}

const TYPE_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  SUBSCRIPTION: { bg: '#dbeafe', text: '#1e40af', label: 'Subscription' },
  BILL: { bg: '#fef3c7', text: '#92400e', label: 'Bill' },
  INCOME: { bg: '#d1fae5', text: '#065f46', label: 'Income' },
  LOAN_PAYMENT: { bg: '#ede9fe', text: '#5b21b6', label: 'Loan Payment' },
  SPLITTED_PAYMENT: { bg: '#fce7f3', text: '#9d174d', label: 'Splitted Payment' },
};

const ALL_RECURRING_TYPES: RecurringType[] = [
  'SUBSCRIPTION',
  'BILL',
  'INCOME',
  'LOAN_PAYMENT',
  'SPLITTED_PAYMENT',
];

function formatPeriod(period: string): string {
  const [year, month] = period.split('-');
  const date = new Date(Number(year), Number(month) - 1);
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

function ProposalRow({
  proposal,
  entityId,
  formatCurrency,
  onConfirm,
  onReject,
  isConfirming,
  isRejecting,
  selectedType,
}: {
  proposal: ProposedPaymentDetail;
  entityId: string;
  formatCurrency: (n: number) => string;
  onConfirm: (entityId: string, paymentId: string, type?: RecurringType) => void;
  onReject: (entityId: string, paymentId: string) => void;
  isConfirming: boolean;
  isRejecting: boolean;
  selectedType?: RecurringType;
}) {
  const expected = proposal.expectedAmount;
  const actual = proposal.actualAmount;
  const amountsDiffer = Math.abs(expected - actual) > 0.01;
  const diffPercent =
    expected > 0
      ? ((actual - expected) / expected) * 100
      : 0;

  return (
    <div className="rounded-md bg-white/70 p-3 space-y-2">
      {/* Period + amounts */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-xs font-medium text-gray-700">
          {formatPeriod(proposal.period)}
        </span>
        <div className="flex items-center gap-3 text-xs">
          <span className="text-gray-500">
            <Banknote className="inline h-3 w-3 mr-0.5 text-gray-400" />
            {formatCurrency(expected)}
          </span>
          <span className="text-gray-500">
            <TrendingUp className="inline h-3 w-3 mr-0.5 text-gray-400" />
            <span style={{ color: amountsDiffer ? '#d97706' : '#16a34a' }}>
              {formatCurrency(actual)}
            </span>
          </span>
          {amountsDiffer && (
            <span
              className="rounded-full px-1.5 py-0.5 text-[10px] font-medium"
              style={{
                backgroundColor: Math.abs(diffPercent) > 10 ? '#fef3c7' : '#f0fdf4',
                color: Math.abs(diffPercent) > 10 ? '#92400e' : '#166534',
              }}
            >
              {diffPercent > 0 ? '+' : ''}
              {diffPercent.toFixed(1)}%
            </span>
          )}
        </div>
      </div>

      {/* Transaction info */}
      {(proposal.transactionPayee || proposal.paidAt) && (
        <div className="text-[11px] text-gray-500 space-y-0.5">
          {proposal.transactionPayee && (
            <div>
              <span className="text-gray-400">Payee:</span>{' '}
              <span className="text-gray-700">{proposal.transactionPayee}</span>
            </div>
          )}
          {proposal.transactionDescription &&
            proposal.transactionDescription !== proposal.transactionPayee && (
              <div>
                <span className="text-gray-400">Desc:</span>{' '}
                <span className="text-gray-700">{proposal.transactionDescription}</span>
              </div>
            )}
          {proposal.paidAt && (
            <div className="flex items-center gap-1">
              <Calendar className="h-3 w-3 text-gray-400" />
              <span>{format(new Date(proposal.paidAt), 'MMM d, yyyy')}</span>
            </div>
          )}
        </div>
      )}

      {/* Installment info for SPLITTED_PAYMENT */}
      {proposal.installmentNumber !== undefined && (
        <div className="text-[11px] text-pink-700 font-medium">
          Installment #{proposal.installmentNumber}
        </div>
      )}

      {/* Per-proposal actions */}
      <div className="flex items-center gap-2 pt-1">
        <Button
          variant="primary"
          size="sm"
          onClick={() => onConfirm(entityId, proposal.paymentId, selectedType)}
          disabled={isConfirming || isRejecting}
          isLoading={isConfirming}
          className="flex-1 !bg-emerald-600 hover:!bg-emerald-700 focus:!ring-emerald-500"
        >
          <Check className="h-3.5 w-3.5 mr-1" />
          Confirm
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onReject(entityId, proposal.paymentId)}
          disabled={isConfirming || isRejecting}
          isLoading={isRejecting}
          className="flex-1"
        >
          <X className="h-3.5 w-3.5 mr-1" />
          Reject
        </Button>
      </div>
    </div>
  );
}

export function RecurringGroupCard({
  group,
  onConfirm,
  onReject,
  onViewEntity,
  confirmingPaymentId,
  rejectingPaymentId,
}: RecurringGroupCardProps) {
  const { formatCurrency: formatCurrencyRaw } = useLocale();
  const [expanded, setExpanded] = useState(false);
  const [selectedType, setSelectedType] = useState<RecurringType>(
    group.entityType as RecurringType,
  );
  const [showTypePicker, setShowTypePicker] = useState(false);
  const typePickerRef = useRef<HTMLDivElement>(null);

  const proposals = group.proposals;
  const count = proposals.length;

  // Bind currency from the entity so EUR accounts show "€" not "kr"
  const formatCurrency = (amount: number) => formatCurrencyRaw(amount, group.currency);

  // Close type picker on outside click
  useEffect(() => {
    if (!showTypePicker) return;
    const handler = (e: MouseEvent) => {
      if (typePickerRef.current && !typePickerRef.current.contains(e.target as Node)) {
        setShowTypePicker(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showTypePicker]);

  // Summary amounts: use entity-level expected + sum of actuals
  const totalActual = proposals.reduce((s, p) => s + p.actualAmount, 0);

  return (
    <div className="rounded-lg border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 overflow-hidden">
      <div className="p-4">
        {/* Header row */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-100">
              <RefreshCw className="h-4 w-4 text-blue-600" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-gray-900 truncate text-sm">
                  {group.entityName}
                </span>
                {/* Type picker dropdown */}
                <div className="relative" ref={typePickerRef}>
                  <button
                    type="button"
                    onClick={() => setShowTypePicker((prev) => !prev)}
                    className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium uppercase transition-opacity hover:opacity-80"
                    style={{
                      backgroundColor: (TYPE_STYLES[selectedType] || TYPE_STYLES.SUBSCRIPTION).bg,
                      color: (TYPE_STYLES[selectedType] || TYPE_STYLES.SUBSCRIPTION).text,
                    }}
                    title="Click to change type before confirming"
                  >
                    {(TYPE_STYLES[selectedType] || { label: selectedType }).label}
                    <ChevronDown className="ml-1 h-2.5 w-2.5 opacity-60" />
                  </button>
                  {showTypePicker && (
                    <div className="absolute z-20 mt-1 left-0 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[160px]">
                      {ALL_RECURRING_TYPES.map((t) => {
                        const s = TYPE_STYLES[t];
                        const isSelected = t === selectedType;
                        return (
                          <button
                            key={t}
                            type="button"
                            onClick={() => {
                              setSelectedType(t);
                              setShowTypePicker(false);
                            }}
                            className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 flex items-center gap-2 ${isSelected ? 'bg-gray-100 font-medium' : ''}`}
                          >
                            <span
                              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                              style={{ backgroundColor: s.text }}
                            />
                            {s.label}
                            {isSelected && <Check className="h-3 w-3 ml-auto text-gray-500" />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                {count} pending match{count !== 1 ? 'es' : ''}
                {' \u00b7 '}
                Expected {formatCurrency(group.expectedAmount)}/ea
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onViewEntity(group.recurringEntityId)}
            title="View recurring entity"
            className="shrink-0"
          >
            <Eye className="h-3.5 w-3.5" />
          </Button>
        </div>

        {/* Summary amounts */}
        <div className="flex items-center gap-4 mb-3 rounded-md bg-white/70 px-3 py-2 flex-wrap">
          <div className="flex items-center gap-1.5 text-sm">
            <Banknote className="h-3.5 w-3.5 text-gray-400" />
            <span className="text-gray-500">Expected:</span>
            <span className="font-medium text-gray-900">
              {formatCurrency(group.expectedAmount * count)}
            </span>
          </div>
          <div className="flex items-center gap-1.5 text-sm">
            <TrendingUp className="h-3.5 w-3.5 text-gray-400" />
            <span className="text-gray-500">Actual:</span>
            <span className="font-medium" style={{ color: '#16a34a' }}>
              {formatCurrency(totalActual)}
            </span>
          </div>
        </div>

        {/* Expand/collapse proposals */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 mb-3"
        >
          {expanded ? (
            <ChevronUp className="h-3 w-3" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          )}
          {expanded ? 'Hide' : 'Show'} transaction details ({count})
        </button>

        {expanded && (
          <div className="space-y-2 mb-3">
            {proposals.map((p) => (
              <ProposalRow
                key={p.paymentId}
                proposal={p}
                entityId={group.recurringEntityId}
                formatCurrency={formatCurrency}
                onConfirm={onConfirm}
                onReject={onReject}
                isConfirming={confirmingPaymentId === p.paymentId}
                isRejecting={rejectingPaymentId === p.paymentId}
                selectedType={selectedType !== group.entityType ? selectedType : undefined}
              />
            ))}
          </div>
        )}

        {/* Bulk confirm all when collapsed */}
        {!expanded && (
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                const typeToPass = selectedType !== group.entityType ? selectedType : undefined;
                proposals.forEach((p) =>
                  onConfirm(group.recurringEntityId, p.paymentId, typeToPass),
                );
              }}
              disabled={!!confirmingPaymentId || !!rejectingPaymentId}
              className="flex-1 !bg-emerald-600 hover:!bg-emerald-700 focus:!ring-emerald-500"
            >
              <Check className="h-3.5 w-3.5 mr-1" />
              Confirm All ({count})
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                proposals.forEach((p) =>
                  onReject(group.recurringEntityId, p.paymentId),
                );
              }}
              disabled={!!confirmingPaymentId || !!rejectingPaymentId}
              className="flex-1"
            >
              <X className="h-3.5 w-3.5 mr-1" />
              Reject All
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
